// ============================================================
// Datei: src/components/guestList/EditGuestModal.jsx
//
// Zweck:
// - Modal zum Bearbeiten / Erfassen eines Gastes
// - Wird vom Parent z. B. KasseDesktop / KasseMobile gesteuert
//
// ÄNDERUNGEN:
// - Alter Rollenfilter roleId >= 80 entfernt
// - Rollenauswahl zeigt jetzt exakt:
//   55 SUPERPARTNER
//   56 PARTNER
//   80 GAST_OK
//   81 GAST_ANWOHNER
//   82 GAST_BAND
//   83 GAST_PETZI
//   84 GAST_FESTIVAL
//   85 TECHNIKER
//   86 PRESSE
// - SUPERPARTNER und PARTNER werden dadurch sicher angezeigt
// - Fallback ergänzt, falls /roles nicht alle Rollen zurückliefert
// - Admissions ohne "Kein Eintritt" werden nicht angezeigt
// - POST-Endpunkt auf /guests vereinheitlicht
// ============================================================

import React, { useEffect, useState } from "react";
import Api from "../../Api";
import Toast from "../Toast";
import "../../styles/editForm.css";

// ============================================================
// ERLAUBTE ROLLEN FÜR GÄSTE
//
// Wichtig:
// - Diese IDs müssen exakt zu deiner Tabelle role passen.
// - Diese Liste ist die zentrale Quelle für das Dropdown.
// - Dadurch werden SUPERPARTNER und PARTNER auch angezeigt,
//   wenn der /roles-Endpunkt sie nicht zurückliefert.
// ============================================================
const GUEST_ROLE_OPTIONS = [
	{
		roleId: 55,
		name: "SUPERPARTNER",
		label: "Superpartner"
	},
	{
		roleId: 56,
		name: "PARTNER",
		label: "Partner"
	},
	{
		roleId: 80,
		name: "GAST_OK",
		label: "Gast-OK"
	},
	{
		roleId: 81,
		name: "GAST_ANWOHNER",
		label: "Gast-Anwohner"
	},
	{
		roleId: 82,
		name: "GAST_BAND",
		label: "Gast-Band"
	},
	{
		roleId: 83,
		name: "GAST_PETZI",
		label: "Gast-Petzi"
	},
	{
		roleId: 84,
		name: "GAST_FESTIVAL",
		label: "Gast-Festival"
	},
	{
		roleId: 85,
		name: "TECHNIKER",
		label: "Techniker"
	},
	{
		roleId: 86,
		name: "PRESSE",
		label: "Presse"
	}
];

// ============================================================
// Helper: Text normalisieren
// ============================================================
const normalizeText = (value) => {
	return String(value || "").trim();
};

const normalizeUpper = (value) => {
	return normalizeText(value).toUpperCase();
};

// ============================================================
// Helper: Rollen-ID robust lesen
//
// Zweck:
// - Je nach Backend-DTO kann die ID roleId, role_id oder id heissen.
// ============================================================
const getRoleId = (roleItem) => {
	const rawId =
		roleItem?.roleId ??
		roleItem?.role_id ??
		roleItem?.id ??
		null;

	if (rawId === null || rawId === undefined || rawId === "") {
		return null;
	}

	const numberValue = Number(rawId);

	return Number.isNaN(numberValue) ? null : numberValue;
};

// ============================================================
// Helper: Rollenname robust lesen
//
// Zweck:
// - Je nach Backend-DTO kann der Name role, name, roleName,
//   role_name oder authority heissen.
// ============================================================
const getRoleName = (roleItem) => {
	return normalizeUpper(
		roleItem?.role ||
		roleItem?.name ||
		roleItem?.roleName ||
		roleItem?.role_name ||
		roleItem?.authority ||
		""
	);
};

// ============================================================
// Helper: Anzeigename der Rolle holen
// ============================================================
const getRoleLabel = (roleItem) => {
	const roleId = getRoleId(roleItem);
	const roleName = getRoleName(roleItem);

	const configuredRole = GUEST_ROLE_OPTIONS.find((roleOption) => {
		return roleOption.roleId === roleId || roleOption.name === roleName;
	});

	if (configuredRole) {
		return configuredRole.label;
	}

	return roleName
		.replace("GAST_", "Gast-")
		.replaceAll("_", " ");
};

// ============================================================
// Helper: Rollen aus API mit fixer Fallback-Liste zusammenführen
//
// Zweck:
// - Die Reihenfolge kommt immer aus GUEST_ROLE_OPTIONS.
// - Wenn /roles Daten liefert, werden diese genutzt.
// - Wenn /roles eine Rolle nicht liefert, bleibt sie trotzdem sichtbar.
// ============================================================
const mergeApiRolesWithFallbackRoles = (apiRoles) => {
	return GUEST_ROLE_OPTIONS.map((roleOption) => {
		const apiRole = apiRoles.find((roleItem) => {
			const roleId = getRoleId(roleItem);
			const roleName = getRoleName(roleItem);

			return roleId === roleOption.roleId || roleName === roleOption.name;
		});

		if (!apiRole) {
			return {
				roleId: roleOption.roleId,
				name: roleOption.name,
				label: roleOption.label
			};
		}

		return {
			...apiRole,
			roleId: getRoleId(apiRole) ?? roleOption.roleId,
			name: getRoleName(apiRole) || roleOption.name,
			label: roleOption.label
		};
	});
};

const EditGuestModal = ({ onClose, onSaved, guest }) => {
	// ------------------------------------------------------------
	// Flags
	// ------------------------------------------------------------
	const isEdit = Boolean(guest);

	// ------------------------------------------------------------
	// Formular-State
	// ------------------------------------------------------------
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		admissionId: "",
		roleId: "",
		recordedById: localStorage.getItem("volunteerId")
	});

	// ------------------------------------------------------------
	// Auswahl-Daten
	// ------------------------------------------------------------
	const [admissions, setAdmissions] = useState([]);
	const [roles, setRoles] = useState([]);

	// ------------------------------------------------------------
	// UI-State
	// ------------------------------------------------------------
	const [showToast, setShowToast] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// ------------------------------------------------------------
	// Initialisierung
	// ------------------------------------------------------------
	useEffect(() => {
		// Bestehende Daten beim Bearbeiten laden
		if (isEdit && guest) {
			setFormData({
				firstName: guest.firstName || "",
				lastName: guest.lastName || "",
				admissionId: guest.admissionId || "",
				roleId: guest.roleId || "",
				recordedById: guest.recordedById || localStorage.getItem("volunteerId")
			});
		}

		loadAdmissions();
		loadRoles();
	}, [guest, isEdit]);

	// ============================================================
	// Admissions laden
	// ============================================================
	const loadAdmissions = async () => {
		try {
			const res = await Api.get("/admissions");
			const apiAdmissions = Array.isArray(res.data) ? res.data : [];

			const filteredAdmissions = apiAdmissions.filter((admissionItem) => {
				const value = admissionItem.admission?.toLowerCase().trim();

				// "Kein Eintritt" soll nicht im Dropdown erscheinen
				return value && !value.includes("kein");
			});

			setAdmissions(filteredAdmissions);
		} catch (error) {
			console.error("❌ Fehler beim Laden der Eintritte:", error);
			setAdmissions([]);
		}
	};

	// ============================================================
	// Rollen laden
	//
	// Wichtig:
	// - Kein Filter roleId >= 80 mehr.
	// - Dadurch werden 55 SUPERPARTNER und 56 PARTNER angezeigt.
	// - Die Dropdown-Liste wird immer aus GUEST_ROLE_OPTIONS aufgebaut.
	// ============================================================
	const loadRoles = async () => {
		try {
			const res = await Api.get("/roles");
			const apiRoles = Array.isArray(res.data) ? res.data : [];

			const mergedRoles = mergeApiRolesWithFallbackRoles(apiRoles);

			setRoles(mergedRoles);
		} catch (error) {
			console.error("❌ Fehler beim Laden der Rollen:", error);

			// Fallback: alle gewünschten Rollen trotzdem anzeigen
			setRoles(GUEST_ROLE_OPTIONS);
		}
	};

	// ------------------------------------------------------------
	// Input Handler
	// ------------------------------------------------------------
	const handleChange = (event) => {
		const { name, value } = event.target;

		setFormData((previousData) => ({
			...previousData,
			[name]: value
		}));
	};

	// ------------------------------------------------------------
	// Submit
	// ------------------------------------------------------------
	const handleSubmit = async (event) => {
		event.preventDefault();

		if (isSaving) {
			return;
		}

		try {
			setIsSaving(true);

			const payload = {
				firstName: formData.firstName.trim(),
				lastName: formData.lastName.trim(),
				admissionId: Number(formData.admissionId),
				roleId: Number(formData.roleId),
				recordedById: Number(formData.recordedById)
			};

			if (isEdit) {
				await Api.put(`/guests/${guest.guestId}`, payload);
			} else {
				await Api.post("/guests", payload);
			}

			setShowToast(true);

			if (onSaved) {
				onSaved();
			}

			// Modal nach kurzer Zeit schließen
			setTimeout(() => {
				if (onClose) {
					onClose();
				}
			}, 800);
		} catch (err) {
			console.error("❌ Fehler beim Speichern des Gastes:", err);
			alert("Fehler beim Speichern.");
		} finally {
			setIsSaving(false);
		}
	};

	// ------------------------------------------------------------
	// Render
	// ------------------------------------------------------------
	return (
		<>
			{showToast && (
				<Toast
					message="Gespeichert!"
					onClose={() => setShowToast(false)}
				/>
			)}

			<div className="modal-overlay">
				<div className="modal-content">
					<h3 className="editform-heading">
						{isEdit ? "Gast bearbeiten" : "Gast erfassen"}
					</h3>

					<form
						onSubmit={handleSubmit}
						className="editform-form"
					>
						<input
							className="editform-input"
							name="firstName"
							placeholder="Vorname"
							value={formData.firstName}
							onChange={handleChange}
							required
						/>

						<input
							className="editform-input"
							name="lastName"
							placeholder="Nachname"
							value={formData.lastName}
							onChange={handleChange}
							required
						/>

						<select
							className="editform-input"
							name="admissionId"
							value={formData.admissionId}
							onChange={handleChange}
							required
						>
							<option value="">
								Eintritt auswählen
							</option>

							{admissions.map((admission) => (
								<option
									key={admission.admissionId}
									value={admission.admissionId}
								>
									{admission.admission}
								</option>
							))}
						</select>

						<select
							className="editform-input"
							name="roleId"
							value={formData.roleId}
							onChange={handleChange}
							required
						>
							<option value="">
								Rolle auswählen
							</option>

							{roles.map((roleItem) => {
								const currentRoleId = getRoleId(roleItem);

								return (
									<option
										key={currentRoleId}
										value={currentRoleId}
									>
										{getRoleLabel(roleItem)}
									</option>
								);
							})}
						</select>

						<div className="modal-buttons">
							<button
								type="submit"
								disabled={isSaving}
							>
								{isSaving ? "Speichern..." : "Speichern"}
							</button>

							<button
								type="button"
								onClick={onClose}
								disabled={isSaving}
							>
								Abbrechen
							</button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default EditGuestModal;